import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { buildUseStyles, useTheme } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { Paper, Tooltip } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4';
import fr from 'facet-rest';
import Title from 'Components/ComponentLibrary/Atoms/Text/Title';
import AccountCard from 'Components/Accounts/AccountCard';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import { wizardClasses } from 'Views/Page/Content/Templates/WizardTemplates/wizardCommonStyles';
import moment from 'moment';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';

const useStyles = buildUseStyles('TransferCompleteTemplateContent', (theme) => ({
  root: {
    ...wizardClasses.root(theme),
  },
  container: {
    ...wizardClasses.container(theme),
  },
  content: {
    ...wizardClasses.content(theme),
  },
  gap: {
    marginBottom: theme.spacing(2),
  },
  logo: {
    height: 90,
  },
  infoItem: {
    marginBottom: theme.spacing(1),
    minWidth: 0,
  },
  title: {
    marginBottom: theme.spacing(4),
  },
  info: {
    width: '100%',
    display: 'grid',
    backgroundColor: theme.palette.facet.backgroundGray,
    padding: theme.spacing(2),
    gridTemplateColumns: 'repeat(2, 50%)',
  },
  infoField: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    marginRight: theme.spacing(1),
  },
  combinedField: {
    display: 'flex',
    flexDirection: 'row',
  },
  ellipsisContainer: {
    minWidth: 0,
  },
  ellipsis: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}));

const TransferCompleteTemplateContent = ({
  className,
  resource,
}) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const logoSrc = resource.relationship(fr.relationships.facetIcon.name).link('related').url();
  const date = moment(resource.attr('transferDate')).format('L');

  return (
    <Box className={classNames(classes.root, className)}>
      <Box className={classes.container}>
        <Box className={classes.content}>
          <img
            className={classNames(classes.logo, classes.gap)}
            src={logoSrc}
            alt={'facet-logo'}
          />
          <Title
            className={classes.title}
            text={resource.attr('title')}
          />
          <AccountCard
            className={classes.gap}
            iconSrc={logoSrc}
            accountName={resource.attr('transferDestinationAccountName')}
            maskedAccountNumber={resource.attr('transferDestinationMaskedAccountNumber')}
          />
          <Paper
            className={classNames(classes.info, classes.gap)}
            rounded
          >
            {resource.attr('amount') && (
            <Box className={classes.infoItem}>
              <Typography>
                {'Amount'}
              </Typography>
              <Typography className={classes.infoField} variant={'h5'} color={'primary'} weight={'medium'}>
                {resource.attr('amount')}
              </Typography>
            </Box>
            )}
            {date && (
            <Box className={classes.infoItem}>
              <Typography>
                {'Date'}
              </Typography>
              <Typography className={classes.infoField} variant={'h5'} color={'primary'} weight={'medium'}>
                {date}
              </Typography>
            </Box>
            )}
            {(resource.attr('transferSourceInstitution') || resource.attr('transferSourceMaskedAccountNumber')) && (
              <Box className={classes.infoItem}>
                <Typography>
                  {'Transfer Source'}
                </Typography>
                <Box className={classNames(classes.infoField, classes.combinedField)}>
                  <Box className={classes.ellipsisContainer}>
                    <Tooltip title={resource.attr('transferSourceInstitution')}>
                      <Typography className={classes.ellipsis} variant={'h5'} color={'primary'} weight={'medium'}>
                        {resource.attr('transferSourceInstitution')}
                      </Typography>
                    </Tooltip>
                  </Box>
                  <Typography variant={'h5'} color={'primary'} weight={'medium'}>
                    {resource.attr('transferSourceMaskedAccountNumber')}
                  </Typography>
                </Box>
              </Box>
            )}
            {resource.attr('status') && (
              <Box className={classes.infoItem}>
                <Typography>
                  {'Status'}
                </Typography>
                <Typography className={classes.infoField} variant={'h5'} color={'primary'} weight={'medium'}>
                  {resource.attr('status')}
                </Typography>
              </Box>
            )}
          </Paper>
          <MetaControlField attr={'subtitle'} />
        </Box>
      </Box>
    </Box>
  );
};

TransferCompleteTemplateContent.defaultProps = {
  className: '',
};

TransferCompleteTemplateContent.propTypes = {
  className: PropTypes.string,
  resource: PropTypes.instanceOf(fr.Resource).isRequired,
};

export default TransferCompleteTemplateContent;
